import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { FileText, Pencil, ScanLine, Layers, Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const BASE = import.meta.env.BASE_URL

const translations = {
  el: {
    hero: {
      title: 'Εισαγωγή & Ανάλυση DXF / STEP',
      subtitle: 'Εισάγετε αρχεία DXF ή STEP και αφήστε το Workshop Assistant να εξάγει αυτόματα διαστάσεις, οπές, κάμψεις και περιμέτρους — έτοιμα για κοστολόγηση.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Δείτε τις Λειτουργίες',
    },
    dxf: {
      title: 'Εισαγωγή DXF',
      body: 'Εισάγετε αρχεία DXF με χειροκίνητη εισαγωγή πάχους υλικού. Το Workshop Assistant αναλύει αυτόματα τη γεωμετρία — περίγραμμα, οπές και γραμμές κάμψης — έτοιμη για επεξεργασία στον Επεξεργαστή Γεωμετρίας.',
    },
    step: {
      title: 'Ανάλυση STEP',
      body: 'Εισάγετε αρχεία STEP με αυτόματη ανίχνευση πάχους από το ίδιο το μοντέλο. Ιδανικό για assemblies όπου δεν θέλετε να εισάγετε πάχος χειροκίνητα για κάθε εξάρτημα.',
    },
    features: {
      title: 'Τι εξάγεται αυτόματα',
      items: [
        { icon: 'file', title: 'Γεωμετρική Ανάλυση', description: 'Διαστάσεις, οπές, κάμψεις και περίμετρος εξάγονται αυτόματα από κάθε εισαγόμενο αρχείο.' },
        { icon: 'pencil', title: 'Επεξεργαστής Γεωμετρίας', description: 'Ταξινομήστε οπτικά περιγράμματα, χαρακτηριστικά και γραμμές κάμψης απευθείας πάνω στη γεωμετρία με κλικ.' },
        { icon: 'scan', title: 'Αυτόματη Ανίχνευση Οπών', description: 'Οπές και κύκλοι ανιχνεύονται αυτόματα, μαζί με καμπύλες ακμές και λοξοτομές.' },
        { icon: 'layers', title: 'Μαζική Επεξεργασία', description: 'Εισάγετε πολλαπλά αρχεία ταυτόχρονα με επιλογή υλικού κατά την εισαγωγή.' },
      ],
    },
    faq: [
      { q: 'Ποια η διαφορά μεταξύ εισαγωγής DXF και STEP;', a: 'Το DXF απαιτεί χειροκίνητη εισαγωγή πάχους υλικού, ενώ το STEP ανιχνεύει το πάχος αυτόματα από το 3D μοντέλο.' },
      { q: 'Ανιχνεύει αυτόματα τις κάμψεις και τις οπές;', a: 'Ναι, τόσο σε DXF όσο και σε STEP αρχεία, με δυνατότητα χειροκίνητης διόρθωσης στον Επεξεργαστή Γεωμετρίας.' },
      { q: 'Μπορώ να εισάγω πολλά αρχεία ταυτόχρονα;', a: 'Ναι, η μαζική επεξεργασία σας επιτρέπει να εισάγετε πολλαπλά αρχεία DXF ή STEP σε ένα βήμα.' },
    ],
    cta: { title: 'Δοκιμάστε την Εισαγωγή CAD', subtitle: 'Κατεβάστε τη δωρεάν δοκιμή και εισάγετε τα δικά σας αρχεία DXF/STEP σήμερα.', btn: 'Δωρεάν Δοκιμή' },
  },
  en: {
    hero: {
      title: 'DXF & STEP Import and Analysis',
      subtitle: 'Import DXF or STEP files and let Workshop Assistant automatically extract dimensions, holes, bends, and perimeters — ready for costing.',
      cta1: 'Download Free Trial',
      cta2: 'Explore Features',
    },
    dxf: {
      title: 'DXF Import',
      body: 'Import DXF files with manual material thickness input. Workshop Assistant automatically analyzes the geometry — outline, holes, and bend lines — ready for editing in the Geometry Editor.',
    },
    step: {
      title: 'STEP Analysis',
      body: 'Import STEP files with automatic thickness detection straight from the model itself. Ideal for assemblies where you don\'t want to enter thickness manually for every part.',
    },
    features: {
      title: "What's extracted automatically",
      items: [
        { icon: 'file', title: 'Geometry Analysis', description: 'Dimensions, holes, bends, and perimeter are extracted automatically from every imported file.' },
        { icon: 'pencil', title: 'Geometry Editor', description: 'Visually classify contours, features, and bend lines directly on the geometry with a click.' },
        { icon: 'scan', title: 'Automatic Hole Detection', description: 'Holes and circles are detected automatically, along with curved edges and chamfers.' },
        { icon: 'layers', title: 'Batch Processing', description: 'Import multiple files at once with material selection during import.' },
      ],
    },
    faq: [
      { q: 'What\'s the difference between DXF and STEP import?', a: 'DXF requires manual material thickness input, while STEP auto-detects thickness from the 3D model.' },
      { q: 'Does it auto-detect bends and holes?', a: 'Yes, for both DXF and STEP files, with manual correction available in the Geometry Editor.' },
      { q: 'Can I import multiple files at once?', a: 'Yes, batch processing lets you import multiple DXF or STEP files in one step.' },
    ],
    cta: { title: 'Try CAD Import Yourself', subtitle: 'Download the free trial and import your own DXF/STEP files today.', btn: 'Download Free Trial' },
  },
}

const icons = { file: <FileText className="h-10 w-10" />, pencil: <Pencil className="h-10 w-10" />, scan: <ScanLine className="h-10 w-10" />, layers: <Layers className="h-10 w-10" /> }

const DxfStepImport = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="DXF & STEP Import Software for Sheet Metal"
        description="Import DXF files with manual thickness or STEP files with auto-detected thickness, and let Workshop Assistant analyze geometry automatically."
        path="/dxf-step-import"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/features" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h2 className="text-3xl font-bold mb-4">{t.dxf.title}</h2>
            <p className="text-lg text-gray-600">{t.dxf.body}</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img src={`${BASE}screenshots/dxf-editor.png?v=2`} alt={t.dxf.title} className="w-full h-auto object-cover" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="lg:order-2">
            <h2 className="text-3xl font-bold mb-4">{t.step.title}</h2>
            <p className="text-lg text-gray-600">{t.step.body}</p>
          </div>
          <div className="lg:order-1 rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img src={`${BASE}screenshots/step-import.png?v=2`} alt={t.step.title} className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.features.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.items.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="card">
              <div className="text-primary-600 mb-4">{icons[f.icon]}</div>
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-gray-600">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default DxfStepImport
